import React, { useEffect, useState } from 'react';
import CodeGenerator from '../code/generator/CodeGenerator';
import StageShell from './common/StageShell';
import {
  getProject,
  setStage,
  getSelectedArchitecture,
} from '../../services/sdlcProject.service';
import { getCodeHistory, getCodeById } from '../../services/codegen.service';

/**
 * SDLC – Code stage.
 *
 * Reuses the existing CodeGenerator screen, but augments it with two SDLC
 * features:
 *   1. A banner showing the architecture decision so the user can craft
 *      their input prompt accordingly.
 *   2. A "bind generated code" picker that pulls the most recent entry
 *      from code history into the SDLC project so downstream stages
 *      (tests, deployment, docs) can consume it.
 */
function CodeStage() {
  const [project, setProject] = useState(getProject());
  const [history, setHistory] = useState([]);
  const arch = getSelectedArchitecture();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getCodeHistory(10);
        const list = res?.codes || res?.data || [];
        if (alive) setHistory(list);
      } catch {
        /* ignore */
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleBind = async (codeId) => {
    try {
      const res = await getCodeById(codeId);
      const snapshot = res?.code || res?.data || res;
      const next = setStage('code', { codeId, snapshot });
      setProject(next);
    } catch (e) {
      console.error('Failed to load code', e);
    }
  };

  const inputs = [
    { label: 'Idea', value: project?.idea?.title || '' },
    {
      label: 'Requirements',
      value: project?.requirements?.snapshot?.title || project?.requirements?.reportId || '',
    },
    { label: 'Architecture', value: arch?.name || '' },
  ];

  const bound = project?.code?.snapshot;

  return (
    <StageShell
      stageId="code"
      description="Generate code aligned with the chosen architecture, then bind a result to the SDLC project."
      inputs={inputs}
      canContinue={Boolean(bound)}
    >
      {arch && (
        <div style={{
          padding: '10px 14px',
          background: '#eff6ff',
          color: '#1e3a8a',
          borderRadius: 8,
          fontSize: '0.9rem',
          marginBottom: 14,
        }}>
          🏛️ Targeting <strong>{arch.name}</strong> — {arch.summary}
        </div>
      )}

      <CodeGenerator />

      <div style={{ marginTop: 24 }}>
        <h3 style={{ fontSize: '0.95rem', margin: '0 0 8px 0' }}>
          Bind a generated codebase to this SDLC project
        </h3>
        <p style={{ marginTop: 0, color: '#6b7280', fontSize: '0.85rem' }}>
          Pick the most recent generation result so the Tests and Deployment stages can use it.
        </p>

        {bound ? (
          <div style={{ padding: 12, background: '#ecfdf5', borderRadius: 8, color: '#065f46', fontSize: '0.9rem' }}>
            ✅ Bound: <strong>{bound.title || bound.project_name || project.code.codeId}</strong> ({(bound.files || []).length} files)
            {' '}
            <button className="stage-btn" style={{ marginLeft: 12 }} onClick={() => { setStage('code', null); setProject(getProject()); }}>
              Unbind
            </button>
          </div>
        ) : (
          <div className="stage-file-list">
            {history.length === 0 && <div className="stage-empty">No code generations yet — generate above.</div>}
            {history.map((c) => {
              const id = c._id || c.code_id;
              return (
                <button
                  key={id}
                  type="button"
                  className="stage-file-item"
                  style={{ textAlign: 'left', cursor: 'pointer', background: 'transparent' }}
                  onClick={() => handleBind(id)}
                >
                  <span>{c.title || c.project_name || id}</span>
                  <span className="meta">{c.created_at?.slice(0, 10) || ''}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </StageShell>
  );
}

export default CodeStage;
